/**
 * @file IntegrationDeleteDialog.tsx
 * @description Подтверждение удаления экземпляра интеграции (phase19.29,
 * второй проход: окно кабинета вместо системного `window.confirm`).
 *
 * Текст говорит о последствиях: ключи удаляются, сервис перестаёт
 * работать там, где использовал этот экземпляр. Кнопка удаления красная,
 * «Отмена» — второстепенная; окно не закрывается мимо и по Esc во время
 * удаления (AlertDialog). На время анимации закрытия держит последний
 * экземпляр, чтобы текст не «пустел».
 */

import { useState } from "react";

import { Button } from "../ui/button";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import {
  INTEGRATION_KIND_META,
  type PlatformIntegrationDto,
} from "../../lib/api-platform-integrations";

interface Props {
  /** Удаляемый экземпляр; null — окно закрыто. */
  item: PlatformIntegrationDto | null;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function IntegrationDeleteDialog({ item, busy, onCancel, onConfirm }: Props) {
  // Последний показанный экземпляр: пока окно закрывается (анимация), текст
  // не должен превращаться в «Удалить интеграцию «» ()?».
  const [shown, setShown] = useState<PlatformIntegrationDto | null>(item);
  if (item && item !== shown) setShown(item);
  const view = item ?? shown;
  const kindLabel = view ? INTEGRATION_KIND_META[view.kind].label : "";
  const scope = view?.shop ? `магазина «${view.shop.displayName}»` : "всех магазинов платформы";
  return (
    <AlertDialog open={item !== null} onOpenChange={(open) => (!open && !busy ? onCancel() : null)}>
      <AlertDialogContent fullScreen className="max-w-md gap-3">
        <AlertDialogHeader>
          <AlertDialogTitle>
            Удалить интеграцию «{kindLabel}» ({view?.name})?
          </AlertDialogTitle>
          <AlertDialogDescription>
            Ключи доступа будут удалены без возможности восстановления, и {kindLabel} перестанет
            работать для {scope}, если других экземпляров нет. Подключить заново можно будет только с
            новыми ключами.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={busy}>
            Отмена
          </Button>
          {/* Окончательное подтверждение — сплошная красная. */}
          <Button variant="destructive-solid" onClick={onConfirm} loading={busy}>
            Удалить
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
